/**
 * career-cv-print — the print-typography route (`/cv-print?version=N`). A TOP-LEVEL
 * route (outside the shell, so no nav chrome), rendering ONE FIXED CV version via the
 * SAME shared renderer the export routes use (`@career/cv-render-v2` `buildCvRenderModel`
 * — app-architecture §5, one rendering logic). `@page` A4 CSS + `@media print` hide the
 * on-screen controls; the browser's own print → PDF is the product's PDF path (no
 * headless chrome). Reads `?version=` from `location.search` (not a router param).
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { buildCvRenderModel, type CvRenderModel } from "@career/cv-render-v2"
import { I18nToken, apiGet } from "../services"
import type { CareerI18n } from "../../i18n"

type CvInput = Parameters<typeof buildCvRenderModel>[0]

export class CvPrintVm implements ViewModel {
  readonly model: Signal<CvRenderModel | null> = signal<CvRenderModel | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  private readonly version: string

  constructor(i18n: CareerI18n, search: string) {
    this.t = i18n.t
    this.version = new URLSearchParams(search).get("version") ?? ""
    this.renderDeps = [this.model, this.error, this.loaded]
  }

  attach(): () => void {
    void this.load()
    return () => {}
  }

  private async load(): Promise<void> {
    if (!this.version) {
      this.error.set("missing version")
      this.loaded.set(true)
      return
    }
    try {
      const { version } = await apiGet<{ version: { cv: CvInput } | null }>(`/api/cv/versions/${encodeURIComponent(this.version)}`)
      this.model.set(version ? buildCvRenderModel(version.cv) : null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  print(): void {
    if (typeof window !== "undefined") window.print()
  }
}

/**
 * The printed CV is ALWAYS dark ink on white paper — a document, not a themed app
 * surface. All colors are hardcoded (never theme tokens) so the sheet reads identically
 * in the light OR dark app theme, and the printed PDF is theme-independent. On screen the
 * sheet gets an A4 affordance (white page, centered, drop shadow); @media print strips the
 * chrome so the browser's print → PDF renders the bare document.
 */
const PRINT_STYLE = `
  .cv-print-controls { display: flex; gap: .75rem; padding: .75rem 14mm; }
  .cv-print {
    box-sizing: border-box; width: 100%; max-width: 210mm; min-height: 297mm;
    margin: 1.5rem auto 3rem; padding: 18mm 16mm; background: #fff; color: #111;
    font: 11pt/1.5 "Georgia", serif;
    box-shadow: 0 1px 3px rgba(0,0,0,.14), 0 14px 42px -14px rgba(0,0,0,.4); border-radius: 2px;
  }
  .cv-doc-name { font-size: 20pt; margin: 0 0 .1rem; color: #111; }
  .cv-doc-label { font-size: 12pt; color: #555; margin: 0 0 .4rem; }
  .cv-doc-contact { font-size: 9.5pt; color: #444; margin: 0 0 1rem; border-bottom: 1px solid #ccc; padding-bottom: .6rem; }
  .cv-doc-summary { margin: 0 0 1rem; color: #222; }
  .cv-doc-section { margin: 0 0 1rem; break-inside: avoid; }
  .cv-doc-section > h2 { font-size: 12.5pt; letter-spacing: .02em; text-transform: uppercase; color: #111; border-bottom: 1px solid #999; padding-bottom: .15rem; margin: 0 0 .5rem; }
  .cv-doc-entry { margin: 0 0 .7rem; break-inside: avoid; color: #222; }
  .cv-doc-entry-head { display: flex; justify-content: space-between; gap: 1rem; font-weight: 600; color: #111; }
  .cv-doc-entry-meta { color: #666; font-weight: 400; white-space: nowrap; }
  .cv-doc-entry ul { margin: .2rem 0 0; padding-left: 1.1rem; }
  @page { size: A4; margin: 16mm; }
  @media print {
    .cv-print-controls { display: none; }
    .cv-print { max-width: none; min-height: 0; margin: 0; padding: 0; box-shadow: none; border-radius: 0; background: #fff; color: #111; }
  }
`

export class CareerCvPrint extends KitElement<CvPrintVm> {
  protected createViewModel(ctx: KitContext): CvPrintVm {
    return new CvPrintVm(ctx.inject(I18nToken), typeof location !== "undefined" ? location.search : "")
  }

  private section(s: CvRenderModel["sections"][number]): TemplateResult {
    return html`
      <section class="cv-doc-section">
        <h2 lang="de">${s.title}</h2>
        ${s.entries.map(
          (e) => html`
            <div class="cv-doc-entry">
              <div class="cv-doc-entry-head">
                <span lang="de">${e.title}${e.subtitle ? html` — ${e.subtitle}` : nothing}</span>
                ${e.meta ? html`<span class="cv-doc-entry-meta" lang="de">${e.meta}</span>` : nothing}
              </div>
              ${e.lines.length > 0 ? html`<ul>${e.lines.map((l) => html`<li lang="de">${l}</li>`)}</ul>` : nothing}
            </div>
          `,
        )}
      </section>
    `
  }

  protected render(vm: CvPrintVm): TemplateResult {
    const model = vm.model.value
    return html`
      <style>${PRINT_STYLE}</style>
      <div class="cv-print-controls">
        <button data-act="print-cv" lang="de">${vm.t("cv.print.action")}</button>
        <a href="/compass/cv" lang="de">${vm.t("cv.print.back")}</a>
      </div>
      ${!model
        ? html`<div class="cv-print"><p lang="de">${vm.loaded.value ? vm.t("cv.print.notFound") : vm.t("cv.print.loading")}</p></div>`
        : html`
            <article class="cv-print">
              <h1 class="cv-doc-name" lang="de">${model.name}</h1>
              ${model.label ? html`<p class="cv-doc-label" lang="de">${model.label}</p>` : nothing}
              <p class="cv-doc-contact" lang="de">
                ${[model.contact.email, model.contact.phone, model.contact.location, model.contact.url].filter(Boolean).join(" · ")}
              </p>
              ${model.summary ? html`<p class="cv-doc-summary" lang="de">${model.summary}</p>` : nothing}
              ${model.sections.map((s) => this.section(s))}
            </article>
          `}
    `
  }

  protected override actions(vm: CvPrintVm): ActionMap {
    return { "print-cv": () => vm.print() }
  }
}
